export class SeededRNG {
  constructor(seed) {
    this.seed = (seed !== null && seed !== undefined) ? seed : Math.floor(Math.random() * 2 ** 32);
  }

  _next() {
    this.seed = (this.seed + 0x6d2b79f5) >>> 0;
    let z = this.seed;
    z = Math.imul(z ^ (z >>> 15), z | 1);
    z ^= z + Math.imul(z ^ (z >>> 7), z | 61);
    return ((z ^ (z >>> 14)) >>> 0) / 4294967296;
  }

  choices(arr, k) {
    const result = [];
    for (let i = 0; i < k; i++) result.push(arr[Math.floor(this._next() * arr.length)]);
    return result;
  }

  choice(arr) {
    return arr[Math.floor(this._next() * arr.length)];
  }
}

const UPPER_ALNUM = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const LOWER_ALNUM = 'abcdefghijklmnopqrstuvwxyz0123456789';
const DIGITS = '0123456789';

export const randUpperAlnum = (n, rng) => rng.choices(UPPER_ALNUM.split(''), n).join('');
export const randDigits = (n, rng) => rng.choices(DIGITS.split(''), n).join('');
export const randBracketToken = (rng) => '[' + randUpperAlnum(9, rng) + ']';

/**
 * The page title: the SEO title exactly as the user typed it, plus a random
 * 8-character code, e.g. "My Title [K7Q2MXD9]". Never rewritten or truncated.
 */
export const buildPageTitle = (seoTitle, rng) =>
  `${String(seoTitle || '').trim()} [${randUpperAlnum(8, rng)}]`;
/**
 * A mixed 8-character string like "Gfbvj@d#": an uppercase letter, lowercase
 * letters, and two symbols. Symbols are limited to ones that are safe in
 * HTML text, HTML attributes, JSON and JS replacement strings (no & < > " ' $).
 */
const MIXED_SYMBOLS = '@#!=~%';
const LOWER_LETTERS = 'abcdefghijklmnopqrstuvwxyz';
export const randMixedCode = (rng) => {
  const chars = Array.from({ length: 8 }, () => rng.choice(LOWER_LETTERS.split('')));
  chars[0] = chars[0].toUpperCase();
  // Two symbols at different positions in the second half, e.g. "Gfbvj@d#".
  const first = 3 + Math.floor(rng._next() * 3);            // 3, 4 or 5
  const second = first + 2 <= 7 ? first + 1 + Math.floor(rng._next() * (7 - first)) : 7;
  chars[first] = rng.choice(MIXED_SYMBOLS.split(''));
  chars[second] = rng.choice(MIXED_SYMBOLS.split(''));
  return chars.join('');
};

/**
 * Page title for the video-review template: the SEO title exactly as typed,
 * plus a mixed random string in parentheses, e.g. "My Title (Gfbvj@d#)".
 * The same string is reused everywhere the title appears on the page.
 */
export const buildMixedPageTitle = (seoTitle, rng) =>
  `${String(seoTitle || '').trim()} (${randMixedCode(rng)})`;
export const randNextHash = (rng) => randUpperAlnum(13, rng);

/**
 * Generate a random image URL.
 * If customImageBase is provided, appends a two-segment random path + .jpg.
 * Otherwise falls back to default host with two numeric segments.
 */
export const randImageUrl = (host, rng, customImageBase) => {
  if (customImageBase) {
    const base = customImageBase.endsWith('/') ? customImageBase : customImageBase + '/';
    const folder = randUpperAlnum(12, rng);
    const filename = randUpperAlnum(12, rng) + '.jpg';
    return `${base}${folder}/${filename}`;
  }
  return `${host}/${randDigits(12, rng)}/${randDigits(12, rng)}.jpg`;
};

/**
 * Generate a random video URL.
 * If customVideoBase is provided, appends a 22-char alphanumeric filename + .mp4.
 * Otherwise falls back to the default VIDEO_HOST with a 22-char filename.
 */
export const randVideoUrl = (defaultHost, rng, customVideoBase) => {
  if (customVideoBase) {
    const base = customVideoBase.endsWith('/') ? customVideoBase : customVideoBase + '/';
    return `${base}${randUpperAlnum(22, rng)}.mp4`;
  }
  return `${defaultHost}/${randUpperAlnum(22, rng)}.mp4`;
};

/**
 * embedUrl for a video: the same Video URL base + the same random video ID
 * as contentUrl, without the .mp4 extension, e.g.
 *   https://lumoraavena.shop/video/I7S8A0SWAJ2FIF707U93E5.mp4
 *   -> https://lumoraavena.shop/video/I7S8A0SWAJ2FIF707U93E5
 */
export const embedUrlFor = (contentUrl) => String(contentUrl || '').replace(/\.mp4(?=$|[?#])/i, '');

/**
 * Generate a short random alphanumeric suffix like "yvrdp2" (6 chars, lowercase + digits).
 */
export const randFileSuffix = (len = 6) => {
  let result = '';
  for (let i = 0; i < len; i++) {
    result += LOWER_ALNUM[Math.floor(Math.random() * LOWER_ALNUM.length)];
  }
  return result;
};

export const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'page';